import React, { useCallback, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import StatusBadge from "components/common/StatusBadge";
import { DateTime } from "components/common/Date";
import {
  amcAllowsEditPageAccess,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import AmcOverviewTab from "./AmcOverviewTab";
import AmcContractVisitLogsTab from "./AmcVisitLogsTab";
import { useAmcContractOverview } from "./AmcContractOverviewContext";
import AmcCompletionActions from "features/Amc/components/AmcCompletionActions";
import AmcRegenerateInvoiceAction from "features/Amc/components/AmcRegenerateInvoiceAction";
import {
  AmcContractOverviewTabParam,
  amcContractOverviewTabIdFromSearchParam,
  isValidAmcContractOverviewTabParam,
  searchParamForAmcContractOverviewTabId,
} from "./amcContractOverviewTabSearchParams";
import AttachmentsTab from "features/Attachments/components/AttachmentsTab";
import { AttachmentSourceType } from "features/Attachments/constants/attachment.constants";

export default function AmcContractOverviewSection() {
  const { contract, refetch } = useAmcContractOverview();
  const { can } = useAccess();
  const { isSuperAdmin } = useProfile();
  const canUpdate = can(Permissions.AMC.UPDATE);
  const showEdit =
    canUpdate && amcAllowsEditPageAccess(contract?.status, isSuperAdmin);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidAmcContractOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const activeTab = amcContractOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = useCallback(
    (tabId: string) => {
      const param = searchParamForAmcContractOverviewTabId(tabId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!param || param === AmcContractOverviewTabParam.OVERVIEW) {
            next.delete("tab");
          } else {
            next.set("tab", param);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const headerTitle = useMemo(
    () => contract?.title?.trim() || contract?.code?.trim() || "—",
    [contract?.title, contract?.code],
  );
  const headerCode =
    contract?.title?.trim() && contract?.code ? contract.code : null;

  if (!contract) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This contract could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <h4 className="fw-bold">{headerTitle}</h4>
                    {headerCode ? (
                      <p className="text-muted mb-1">
                        <span className="fw-medium">{headerCode}</span>
                      </p>
                    ) : null}
                    <div className="hstack gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Status:</span>
                        <StatusBadge
                          value={contract.status}
                          map={amcStatusMap}
                        />
                      </div>
                      {contract.created_at ? (
                        <>
                          <div className="vr d-none d-sm-block" />
                          <div className="text-muted">
                            Created:{" "}
                            <span className="fw-medium">
                              <DateTime date={contract.created_at} />
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      <AmcCompletionActions
                        contract={contract}
                        onSuccess={refetch}
                      />
                      <AmcRegenerateInvoiceAction
                        contract={contract}
                        onSuccess={refetch}
                      />
                      {showEdit ? (
                        <Link
                          to={`/amc-management/edit/${contract.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "2" },
                          "text-body fw-semibold",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setTabInUrl("2");
                        }}
                        href="#"
                      >
                        Visits
                      </NavLink>
                    </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "3" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("3");
                      }}
                      href="#"
                    >
                      Attachments
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12} className="text-muted">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <AmcOverviewTab />
            </TabPane>
            <TabPane tabId="2">
                <AmcContractVisitLogsTab />
              </TabPane>
            <TabPane tabId="3">
              <AttachmentsTab
                sourceType={AttachmentSourceType.AMC}
                sourceId={contract.id}
                canManage={canUpdate}
              />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
}
